import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

import api from "../services/api";
import toastError from "../errors/toastError";

// Access token lives in memory only (never localStorage). The httpOnly refresh
// cookie renews it — on load and on any 401.
let accessToken = null;

// Per-tenant state cached in localStorage (webphone ramal + credentials, active
// project). These MUST NOT leak across users on a shared browser — when the
// authenticated identity changes we purge them. Returns true if a different user
// than the one cached was detected (caller may need to reload mounted providers).
const LAST_USER_KEY = "nb_last_user";
const TENANT_KEYS = ["nb_webphone", "numera.activeProjectId"];

const purgeTenantState = () => {
  TENANT_KEYS.forEach(k => localStorage.removeItem(k));
};

const reconcileTenant = userId => {
  const id = userId != null ? String(userId) : null;
  const prev = localStorage.getItem(LAST_USER_KEY);
  const changed = !!id && !!prev && prev !== id;
  if (changed) purgeTenantState();
  if (id) localStorage.setItem(LAST_USER_KEY, id);
  return changed;
};

const setAuthHeader = token => {
  accessToken = token;
  if (token) {
    api.defaults.headers.Authorization = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.Authorization;
  }
};

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  // sessão de visualização aberta pelo backoffice (ver como o cliente vê)
  const [viewingAs, setViewingAs] = useState(null);

  useEffect(() => {
    // request interceptor: attach the in-memory access token
    const reqId = api.interceptors.request.use(config => {
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      return config;
    });

    // response interceptor: on 401, try a single silent refresh then retry
    const resId = api.interceptors.response.use(
      res => res,
      async error => {
        const original = error.config;
        const status = error?.response?.status;
        const isRefreshCall = original?.url?.includes("/auth/refresh");

        if (status === 401 && !original._retry && !isRefreshCall) {
          original._retry = true;
          try {
            const { data } = await api.post("/auth/refresh");
            setAuthHeader(data.accessToken);
            original.headers.Authorization = `Bearer ${data.accessToken}`;
            return api(original);
          } catch (refreshErr) {
            setAuthHeader(null);
            setIsAuth(false);
            setUser(null);
            history.push("/login");
            return Promise.reject(refreshErr);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.request.eject(reqId);
      api.interceptors.response.eject(resId);
    };
  }, [history]);

  useEffect(() => {
    // bootstrap session from the refresh cookie
    (async () => {
      try {
        const { data } = await api.post("/auth/refresh");
        // a different user than the one cached on this browser: purge the stale
        // per-tenant local state and reload so the providers (webphone, project)
        // remount clean instead of restoring the previous tenant's ramal/project.
        if (data.user && reconcileTenant(data.user.id)) {
          window.location.reload();
          return;
        }
        setAuthHeader(data.accessToken);
        setUser(data.user);
        setIsAuth(true);
      } catch (err) {
        setAuthHeader(null);
        setIsAuth(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleLogin = async ({ email, password }) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // switching identity on this browser: drop the previous tenant's cached
      // webphone ramal/credentials and active project before mounting the app.
      reconcileTenant(data.user && data.user.id);
      setAuthHeader(data.accessToken);
      setUser(data.user);
      setIsAuth(true);
      history.push("/");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  // Passa a ver o produto com os olhos do cliente. Só o token EM MEMÓRIA é
  // trocado: o cookie de refresh continua sendo o da equipe, então sair (ou
  // simplesmente recarregar a página) devolve a sessão de staff — não há como
  // ficar preso na conta do cliente.
  const startViewingAs = (token, alvo) => {
    // o projeto ativo é por usuário; deixar o da equipe faria a tela abrir num
    // projeto que o cliente não tem
    localStorage.removeItem("numera.activeProjectId");
    setAuthHeader(token);
    setViewingAs(alvo);
    setUser(u => ({ ...(u || {}), id: alvo.id, name: alvo.name, email: alvo.email, isStaff: false }));
    history.push("/");
  };

  const stopViewingAs = async () => {
    try {
      const { data } = await api.post("/auth/refresh");
      localStorage.removeItem("numera.activeProjectId");
      setAuthHeader(data.accessToken);
      setUser(data.user);
      setViewingAs(null);
      history.push("/admin/users");
    } catch (err) {
      // sem sessão de equipe para voltar: melhor exigir login novo do que
      // deixar a tela num estado ambíguo
      setAuthHeader(null);
      setIsAuth(false);
      setViewingAs(null);
      history.push("/login");
    }
  };

  const handleLogout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // ignore — clear local state regardless
    }
    // never leave the webphone ramal/credentials or active project behind for
    // the next person on this browser
    purgeTenantState();
    setAuthHeader(null);
    setIsAuth(false);
    setUser(null);
    history.push("/login");
  };

  return { isAuth, loading, user, setUser, handleLogin, handleLogout, viewingAs, startViewingAs, stopViewingAs };
};

export default useAuth;
